import React, { useContext, SyntheticEvent, FunctionComponent, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

// MUI
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

// MUI Icons
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ContentCopy, Delete, Tour } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import LabelIcon from '@mui/icons-material/Label';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PeopleIcon from '@mui/icons-material/People';

import EditNoteIcon from '@mui/icons-material/EditNote'; // prijavi -> skica
import SendIcon from '@mui/icons-material/Send'; // skica -> prijavi
import PublicIcon from '@mui/icons-material/Public'; // prijavi -> objavi
import PublicOffIcon from '@mui/icons-material/PublicOff'; // objavi -> prijavi

import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

import AddchartIcon from '@mui/icons-material/Addchart';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';

import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';

// Custom Components
import useFormController from '@/components/useFormController';
import DialogHeader from '@/ui/Dialog/DialogHeader';
import DialogFooter from '@/ui/Dialog/DialogFooter';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogBody from '@/ui/Dialog/DialogBody';
import DialogActionButton from '@/ui/Dialog/DialogActionButton';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogToolbarMenuItem from '@/ui/Dialog/DialogToolbarMenuItem';
import DialogContext from '@/ui/DialogContext/DialogContext';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import UserContext from '@/components/UserContext/UserContext';
import { GridContainer, GridItem } from '@/ui/Grid';
import FormContent from '@/components/FormContent';
import { Permissions } from '@/lib/permissions';
import FormAvatar from '@/ui/Form/FormAvatar';
import FormHeading from '@/ui/Form/FormHeading';
import { FormTab, FormTabPanel, FormTabs } from '@/ui/Form';
import FormHeadingIzletData from '@/ui/Form/FormHeadingIzletData';

import IzletDaniPane from '@/views/Izleti/Forms/Panes/IzletDaniPane';
import IzletOpisPane from '@/views/Izleti/Forms/Panes/IzletOpisPane';
import IzletSudioniciPane from '@/views/Izleti/Forms/Panes/IzletSudioniciPane';
import { sifrarnici } from '@/lib/sifrarnici';

// Types
import { ClosingDetails, FormControllerProps } from '@/@types/components/formController';
import { IDialogProps } from '@/@types/ui/DialogContext';
import { PickerItem } from '@/@types/controls/controls';

const HPSIzletForm: FunctionComponent<IDialogProps> = (props) => {
  // HOOKS
  const dialogContext = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext);
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const {
    record,
    originalRecord,
    validation,
    dataChanged,
    fields,
    subModels,
    onFieldChange,
    doInsert,
    doUpdate,
    doClose,
    doRefresh,
    doDelete,
    doActivate,
    doCustomAction,
  } = useFormController(props as FormControllerProps);

  // STATE
  const [changeMade, setChangeMade] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  const [submitConditions, setSubmitConditions] = useState<{ hasVodic: boolean; hasDanWithGeom: boolean }>({
    hasVodic: false,
    hasDanWithGeom: false,
  });
  const [publishMissingOpisIDs, setPublishMissingOpisIDs] = useState<number[]>(sifrarnici.obavezna_poglavlja_izleta);

  // PROPS
  const { dc, mode, form, recordId, onClose } = props;

  // VARIABLES
  const location = useLocation();
  const allowManage = userContext?.hasAnyPermission([Permissions.HPSPlanVodicEdit]) ? true : false;


  // very hacky but it is what it is
  const allowAdministration = location.pathname.includes('izleti-hps') && userContext?.hasAnyPermission([Permissions.HPSKomisijaView]);
  const allowCopy = form === 'vodic' && mode === 'view' && allowManage;
  const disableSave = dataChanged ? false : true;
  const disablePanes = mode === 'insert' || !record.id;
  const allowEdit = mode === 'update' && allowManage; // keep
  const allowChangeStatus = mode === 'update';
  const canCancel = userContext && userContext.id === record.created_by;
  const allowApproval =
    (record.status_id as number) !== sifrarnici.status_izleta.draft &&
    mode === 'view' &&
    ((record.organizator_udruga_id === userContext?.planUdrugaId && location.pathname.includes('izleti-udruge')) ||
      (record.organizator_udruga_id === userContext?.spvId && location.pathname.includes('izleti-spv')));

  const hasToolbarActions = allowEdit || allowCopy || allowAdministration || allowApproval;

  const formTitle = mode === 'insert' ? (t('titles.izlet_new') as string) : (t('titles.izlet') as string);
  const icon = <Tour />;
  const status = record['status_id'] as number;

  const izletDrzavaId =
    (record === null || record.drzava_id === null) ? null 
    : (record !== null && typeof record.drzava_id === "object") ? (record.drzava_id as PickerItem).value
    : typeof record.drzava_id === "number" ? record.drzava_id
    : null;

  const izletHR = izletDrzavaId === sifrarnici.izlet_drzave.hrvatska; // originalRecord is changed on doRefresh so this is fine

  const canSubmit = submitConditions.hasVodic 
    && ((izletHR && submitConditions.hasDanWithGeom) || !izletHR);

  let cmode = mode;
  if (record.active === false || status === 30) {
    cmode = 'view';
  }

  // USEEFFECTS
  useEffect(() => {
    const handlePopState = (event: any) => {
      // Prevent going back by pushing the state again
      window.history.pushState(null, '', window.location.href);
      dialogContext.hideDialog(); // Call your custom dialog hide function
    };

    // Push state to prevent back navigation
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // CORE ACTIONS
  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const close = (result: ClosingDetails, dontHide: boolean = false) => {
    if (result.dataChanged || dataChanged) {
      onClose({ dataChanged: true, action: result.action });
    }

    if (!dontHide) {
      dialogContext.hideDialog();
    }
  };

  const handleTabChange = (value: number) => {
    if (!disableSave && currentTab === 0 && mode === 'update') {
      handleUpdate();
    }
    if (value === 1) {
      if (record.prvi_dan) {
        setCurrentTab(value);
      } else {
        setCurrentTab(0);
        snackbarContext.showNotification('izleti.warnings.prvi_dan', 'warning');
      }
    } else {
      setCurrentTab(value);
    }
  };

  // HANDLE ACTIONS
  const handleInsert = (evt: SyntheticEvent) => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            snackbarContext.showNotification(t('notifications.insert'), 'success');
            dc.changeApiPath((dc.apiPath as string).replace('?action=copy', '')); // recover from copying if necessary
            dialogContext.updateDialogProps(1, { mode: 'update', recordId: result.id, initialRecord: undefined });
            close({ dataChanged: true, action: 'insert' }, true);
            setCurrentTab(1);
          } else if (result.error) {
            const msg = t('error.addRecord') + result.error;
            snackbarContext.showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            //do nothing
          }
        })
        .catch((resp) => {
          snackbarContext.showNotification(resp.error, 'error');
        });
    }
  };

  const handleUpdate = (evt?: SyntheticEvent) => {
    if (doUpdate) {
      doUpdate().then((result) => {
        if (result.success) {
          snackbarContext.showNotification(t('notifications.update'), 'success');
          setChangeMade(true);
          doRefresh();
          // close({ dataChanged: true, action: 'update' });
        } else if (result.error) {
          const msg = t('error.editRecord') + result.error;
          snackbarContext.showNotification(msg, 'error');
        } else if (result.validationPass === false) {
          // do nothing
        }
      });
    }
  };

  const handleClose = (evt: SyntheticEvent) => {
    if (doClose) {
      doClose()
        .then((result) => {
          if (result.success) {
            close({ dataChanged: changeMade, action: 'cancel' });
          } else {
            if (result.shouldSave) {
              handleUpdate(evt);
            } else if (result.canceled) {
              // do nothing
            }
          }
        })
        .catch((result) => {
          console.log(result);
        });
    }
  };

  const handleActivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        doActivate(true).then((result) => {
          if (result) {
            const msg = t('messages.activate_record_success');
            snackbarContext.showNotification(msg, 'success');
            doRefresh();
            onClose({ dataChanged: true, action: 'update' });
          } else {
            const msg = t('error.activate_record');
            snackbarContext.showNotification(msg, 'error');
          }
        });
      } catch (error) {
        const msg = t('error.activate_record');
        snackbarContext.showNotification(msg, 'error');
      }
    }
  };

  const handleDeactivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        doActivate(false).then((result) => {
          if (result) {
            const msg = t('messages.deactivate_record_success');
            snackbarContext.showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else {
            const msg = t('error.deactiavate_record');
            snackbarContext.showNotification(msg, 'error');
          }
        });
      } catch (error) {
        const msg = t('error.deactivate_record');
        snackbarContext.showNotification(msg, 'error');
      }
    }
  };

  const handleDelete = () => {
    doDelete().then((result) => {
      if (result.success) {
        snackbarContext.showNotification(t('notifications.delete'), 'success');
        close({ dataChanged: true, action: 'delete' });
      } else if (result.error) {
        const msg = t('error.delete') + result.error;
        snackbarContext.showNotification(msg, 'error');
      }
    });
  };

  /*const handlePublishWithWarning = () => { // reuse for admins
    const title = t('cdialogs.are_you_sure_publish_izlet');
    const text = t('cdialogs.texts.izleti_publish');
    const confirmButtonText = t('cdialogs.continue');
    const cancelButtonText = t('cdialogs.cancel');

    dialogContext.showConfirmDialog({ title, text, confirmButtonText, cancelButtonText }).then((result) => {
      if (result.confirmed && !result.canceled) {
        handleStatusChange(sifrarnici.status_izleta.approved);
      }
    });
  };*/

  const handleSubmithWithWarning = () => {
    const title = t('cdialogs.are_you_sure_submit_izlet');
    const text = t('cdialogs.texts.izleti_submit');
    const confirmButtonText = t('cdialogs.continue');
    const cancelButtonText = t('cdialogs.cancel');

    dialogContext.showConfirmDialog({ title, text, confirmButtonText, cancelButtonText }).then((result) => {
      if (result.confirmed && !result.canceled) {
        handleStatusChange(sifrarnici.status_izleta.submitted);
      }
    });
  };

  const handleStatusChange = (newStatusId: number) => {
    doCustomAction('status', { status_id: newStatusId })
      .then((result) => {
        if (result) {
          setChangeMade(true);
          const msg = t('messages.izlet_status_change.' + newStatusId);
          snackbarContext.showNotification(msg, 'success');
          doRefresh();
        } else {
          snackbarContext.showNotification(t('notifications.izlet_error.' + newStatusId), 'warning');
        }
      })
      .catch((e) => {
        snackbarContext.showNotification(t('notifications.izlet_error.' + newStatusId), 'warning');
      });
  };

  const handleCancel = (shouldCancel: boolean) => {
    doCustomAction(shouldCancel ? 'cancel' : 'republish')
      .then((result) => {
        if (result) {
          setChangeMade(true);
          const msg = t('notifications.izlet.cancel.' + shouldCancel);
          snackbarContext.showNotification(msg, 'success');
          doRefresh();
        } else {
          snackbarContext.showNotification(t('notifications.unexpected_error'), 'warning');
        }
      })
      .catch((e) => {
        snackbarContext.showNotification(t('notifications.unexpected_error'), 'warning');
      });
  };

  const handleStatusChangeApprove = (newStatusId: number) => {
    doCustomAction('approve', { approve: newStatusId === sifrarnici.status_izleta.approved })
      .then((response) => {
        if (response.success) {
          const msg = t('messages.izlet_status_change.' + newStatusId);
          snackbarContext.showNotification(msg, 'success');
          doRefresh();
        } else {
          snackbarContext.showNotification(t('notifications.izlet_error.' + newStatusId), 'warning');
        }
      })
      .catch((x) => {
        snackbarContext.showNotification(t('notifications.izlet_error.' + newStatusId), 'warning');
      });
  };

  const handleAproveWithWarning = () => {
    const newStatus = status === sifrarnici.status_izleta.approved ? sifrarnici.status_izleta.submitted : sifrarnici.status_izleta.approved;

    if (newStatus === sifrarnici.status_izleta.submitted) {
      handleStatusChangeApprove(newStatus);
      return;
    }

    const title = t('cdialogs.are_you_sure_approve_izlet');
    const text = t('cdialogs.texts.approve_izlet');
    const confirmButtonText = t('cdialogs.continue');
    const cancelButtonText = t('cdialogs.cancel');

    dialogContext.showConfirmDialog({ title, text, confirmButtonText, cancelButtonText }).then((result) => {
      if (result.confirmed && !result.canceled) {
        handleStatusChangeApprove(newStatus);
      }
    });
  };

  const handleIsStats = (newState: boolean) => {
    doCustomAction('is_stats', { is_stats: newState })
      .then((result) => {
        if (result) {
          setChangeMade(true);
          const msg = t(`notifications.izlet.stats.${newState}`);
          snackbarContext.showNotification(msg, 'success');
          doRefresh();
        } else {
          snackbarContext.showNotification(t('notifications.unexpected_error'), 'warning');
        }
      })
      .catch(() => {
        snackbarContext.showNotification(t('notifications.unexpected_error'), 'warning');
      });
  };

  const handleForceJavno = (is_javno: boolean) => {
    doCustomAction('is_javno', { is_javno })
      .then((result) => {
        if (result) {
          setChangeMade(true);
          const msg = t(`notifications.izlet.force_javno.${is_javno}`);
          snackbarContext.showNotification(msg, 'success');
          doRefresh();
        } else {
          snackbarContext.showNotification(t('notifications.unexpected_error'), 'warning');
        }
      })
      .catch(() => {
        snackbarContext.showNotification(t('notifications.unexpected_error'), 'warning');
      });
  };

  const handleCopy = () => {
    const { odrediste, organizator_udruga_id, drzava_id, trajanje_dani, broj_sudionika, interna_napomena } = record;
    snackbarContext.showNotification('notifications.copying_izlet', 'success');
    dc.changeApiPath(dc.apiPath + '?action=copy');
    dialogContext.updateDialogProps(1, {
      recordId: undefined,
      mode: 'insert',
      initialRecord: {
        id: recordId,
        odrediste,
        organizator_udruga_id,
        drzava_id,
        trajanje_dani,
        broj_sudionika,
        interna_napomena,
      },
    });
  };

  const handleHasVodiciSubmitUvjet = (hasVodic: boolean) => {
    setSubmitConditions((prev) => {
      return { ...prev, hasVodic };
    });
  };

  const handleHasDaniSubmitUvjet = useCallback((hasDanWithGeom: boolean) => {
    setSubmitConditions((prev) => {
      return { ...prev, hasDanWithGeom };
    });
  }, []);

  const handleHasOpisPublishUvjet = useCallback((missingIds: number[]) => {
    setPublishMissingOpisIDs(missingIds);
  }, []);

  //const canPublish = useMemo(() => !publishMissingOpisIDs.length, [publishMissingOpisIDs]);

  const cantSubmitTooltip: ReactNode = useMemo(() => {
    const messages: string[] = [t('tooltip.izleti_status.cant_submit_title')];
    if (!submitConditions.hasVodic) messages.push(t('tooltip.izleti_status.no_vodic'));
    if (!submitConditions.hasDanWithGeom) messages.push(t('tooltip.izleti_status.no_dan'));

    return (
      <Box>
        {messages.map((msg, i) => (
          <Typography key={i} variant="body2">
            {msg}
          </Typography>
        ))}
      </Box>
    );
  }, [submitConditions, t]);

  /*const cantPublishTooltip: ReactNode = useMemo(() => {
    if (!publishMissingOpisIDs.length) return null;

    const poglavljaMap: Record<number, string> = {
      1: 'hps.izlet_poglavlja.opis_non_req',
      2: 'hps.izlet_poglavlja.plan_izleta',
      3: 'hps.izlet_poglavlja.mjesto_vrijeme_polaska',
      4: 'hps.izlet_poglavlja.povratak',
      5: 'hps.izlet_poglavlja.zahtjevnost_ture_non_req',
      6: 'hps.izlet_poglavlja.oprema',
      7: 'hps.izlet_poglavlja.prehrana',
      8: 'hps.izlet_poglavlja.prijevoz',
      9: 'hps.izlet_poglavlja.prijava_non_req',
      10: 'hps.izlet_poglavlja.troskovi',
      11: 'hps.izlet_poglavlja.dokumenti',
      12: 'hps.izlet_poglavlja.odustanak',
      13: 'hps.izlet_poglavlja.dodatne_informacije',
    };

    const missingTitles = publishMissingOpisIDs.map((id) => t(poglavljaMap[id])).join(', ');

    return (
      <Box>
        <Typography variant="body2">{t('tooltip.izleti_status.cant_publish_title')}</Typography>
        <Typography variant="body2">{missingTitles}</Typography>
      </Box>
    );
  }, [publishMissingOpisIDs, t]);*/

  const handleNextStepWithUpdate = (evt: SyntheticEvent) => {
    //console.log("clicked me")
    if (!disableSave && currentTab === 0 && mode === 'update') {
      handleUpdate(evt);
    }
    setCurrentTab((prev) => prev + 1);
  };

  // RENDER
  return (
    <Dialog open fullScreen onClose={handleClose}>
      <DialogHeader>
        <Toolbar variant="dense" disableGutters>
          <FormAvatar icon={icon} ariaLabel="poi" />
          <FormHeading
            header={formTitle}
            subheaderContent={mode !== 'insert' && record.id ? <FormHeadingIzletData record={record} /> : undefined}
          />
          <ToolbarFillContent />
          {hasToolbarActions ? (
            <DialogToolbarMenuButton>
              {allowApproval ? (
                <DialogToolbarMenuItem
                  onClick={() => {
                    handleAproveWithWarning();
                  }}
                  label={t(
                    status === sifrarnici.status_izleta.approved ? 'buttons.izleti_status.unapprove' : 'buttons.izleti_status.approve'
                  )}
                  icon={status === sifrarnici.status_izleta.approved ? <RemoveDoneIcon /> : <DoneAllIcon />}
                />
              ) : null}
              {allowChangeStatus && status === sifrarnici.status_izleta.submitted ? (
                <DialogToolbarMenuItem
                  onClick={() => {
                    handleStatusChange(sifrarnici.status_izleta.draft);
                  }}
                  label={t('buttons.izleti_status.draft')}
                  icon={<EditNoteIcon />}
                />
              ) : null}
              {allowCopy ? (
                <DialogToolbarMenuItem
                  onClick={() => {
                    handleCopy();
                  }}
                  label={t('buttons.copy_izlet')}
                  icon={<ContentCopy />}
                />
              ) : null}
              {mode === 'update' && canCancel && record.is_otkazan === false ? (
                <DialogToolbarMenuItem
                  onClick={() => {
                    handleCancel(true);
                  }}
                  label={t('buttons.cancel_izlet')}
                  icon={<EventBusyIcon />}
                />
              ) : null}
              {mode === 'update' && canCancel && record.is_otkazan === true ? (
                <DialogToolbarMenuItem
                  onClick={() => {
                    handleCancel(false);
                  }}
                  label={t('buttons.cancel_cancel')}
                  icon={<EventAvailableIcon />}
                />
              ) : null}

              {allowAdministration && record.is_javno === false ? (
                <DialogToolbarMenuItem onClick={() => handleForceJavno(true)} label={t('buttons.force_javno')} icon={<PublicIcon />} />
              ) : null}

              {allowAdministration && record.is_javno === true ? (
                <DialogToolbarMenuItem
                  onClick={() => handleForceJavno(false)}
                  label={t('buttons.force_nejavno')}
                  icon={<PublicOffIcon />}
                />
              ) : null}

              {allowAdministration && record.is_stats === false ? (
                <DialogToolbarMenuItem onClick={() => handleIsStats(true)} label={t('buttons.yes_stats')} icon={<AddchartIcon />} />
              ) : null}

              {allowAdministration && record.is_stats === true ? (
                <DialogToolbarMenuItem onClick={() => handleIsStats(false)} label={t('buttons.no_stats')} icon={<DeleteSweepIcon />} />
              ) : null}

              {allowAdministration && record.active === true ? (
                <DialogToolbarMenuItem onClick={handleDeactivate} label={t('buttons.deactivate')} icon={<CancelIcon />} />
              ) : null}
              {allowAdministration && record.active === false ? (
                <DialogToolbarMenuItem onClick={handleActivate} label={t('buttons.activate')} icon={<CheckCircleIcon />} />
              ) : null}
              {mode === 'update' ? <DialogToolbarMenuItem onClick={handleDelete} label={t('buttons.delete')} icon={<Delete />} /> : null}
            </DialogToolbarMenuButton>
          ) : null}

          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogHeader>
      <DialogBody>
        {/* {mode !== 'insert' ? (
          <StatusProgressControl
            field={(dc as dataController).getField('status_id')}
            value={status}
            onChange={handleFieldChange}
            formMode="form"
            controlMode={mode}
          />
        ) : null} */}
        <FormTabs value={currentTab} onChange={handleTabChange}>
          <FormTab
            id="basic-tab"
            label={t('common.tabs.basic')}
            icon={<InfoOutlineIcon />}
            aria-label="basic"
            value={0}
            children={undefined}
          />
          <FormTab
            id="sudionici"
            label={t('titles.izleti_sudionici')}
            icon={<PeopleIcon />}
            aria-label="sudionici"
            value={1}
            disabled={disablePanes}
            children={undefined}
          />
          <FormTab
            id="dani-tab"
            label={t('titles.plan_izleta')}
            icon={<DateRangeIcon />}
            aria-label="dani"
            value={2}
            disabled={disablePanes}
            children={undefined}
          />
          <FormTab
            id="opis"
            label={t('titles.objava_izleta')}
            icon={<LabelIcon />}
            aria-label="opis"
            value={3}
            disabled={disablePanes}
            children={undefined}
          />
        </FormTabs>
        <FormTabPanel value={currentTab} index={0} mountInvisible>
          <Box m={0} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box m={2} maxWidth="lg" alignContent="center">
              <GridContainer>
                <FormContent
                  fieldNames={dc.getFormFieldsNames(form)}
                  record={record}
                  validation={validation}
                  fields={fields}
                  onFieldChange={handleFieldChange}
                  mode={cmode}
                  subModels={subModels}
                  columns={2}
                />
              </GridContainer>
            </Box>
          </Box>
        </FormTabPanel>
        {record.id ? (
          <>
            <FormTabPanel value={currentTab} index={1} mountInvisible>
              <IzletSudioniciPane
                onHasVodiciSubmitUvjet={handleHasVodiciSubmitUvjet}
                setDataChanged={setChangeMade}
                mode={cmode}
                izletId={record.id as number}
              />
            </FormTabPanel>
            <FormTabPanel value={currentTab} index={2} mountInvisible>
              <IzletDaniPane
                onHasDaniSubmitUvjet={handleHasDaniSubmitUvjet}
                variant="hps"
                mode={cmode}
                izletId={record.id as number}
                excursionIsCroatian={izletHR}
                showHPSHelpers
              />
            </FormTabPanel>
            <FormTabPanel value={currentTab} index={3} mountInvisible>
              <IzletOpisPane onHasOpisPublishUvjet={handleHasOpisPublishUvjet} variant="hps" mode={cmode} izletId={record.id as number} />
            </FormTabPanel>
          </>
        ) : null}
      </DialogBody>
      <DialogFooter>
        {cmode === 'view' || cmode === 'update' ? (
          <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        ) : (
          <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        )}
        {currentTab !== 3 ? (
          <>
            {cmode === 'insert' ? <DialogActionButtonAdd next variant="contained" onClick={handleInsert} /> : null}
            {cmode === 'update' ? <DialogActionButtonAdd next variant="contained" onClick={handleNextStepWithUpdate} /> : null}
          </>
        ) : null}
        {allowChangeStatus && status === sifrarnici.status_izleta.draft ? (
          <DialogActionButton
            tooltip={canSubmit ? t('tooltip.izleti_status.submit') : cantSubmitTooltip}
            ariaLabel="submit"
            ariaControls="submit"
            startIcon={<SendIcon />}
            variant="contained"
            disabled={!canSubmit}
            onClick={handleSubmithWithWarning}
          >
            {t('buttons.izleti_status.submit')}
          </DialogActionButton>
        ) : null}
        {/*allowChangeStatus && status === sifrarnici.status_izleta.submitted ? (
          <DialogActionButton
            tooltip={canPublish ? t('tooltip.izleti_status.publish') : cantPublishTooltip}
            ariaLabel="publish"
            ariaControls="publish"
            startIcon={<PublicIcon />}
            variant="contained"
            disabled={!canPublish}
            onClick={handlePublishWithWarning}
          >
            {t('buttons.izleti_status.publish')}
          </DialogActionButton>
        ) : null}
        {allowChangeStatus && status === sifrarnici.status_izleta.published ? (
          <DialogActionButton
            tooltip={t('tooltip.izleti_status.submit')}
            ariaLabel="submit"
            ariaControls="submit"
            startIcon={<PublicOffIcon />}
            variant="contained"
            onClick={() => handleStatusChange(sifrarnici.status_izleta.submitted)}
          >
            {t('buttons.izleti_status.unpublish')}
          </DialogActionButton>
        ) : null*/}
      </DialogFooter>
    </Dialog>
  );
};

export default HPSIzletForm;
